import type { Integration, Event as SentryEvent } from "@sentry/core";

import { nanoid } from "nanoid";
import { pick } from "radash";
import { mergeDeepRight, pipe } from "ramda";

import { getClientSideArgusProjection } from "@solvari/argus";
import { getArgusEvents } from "@solvari/argus/thin-client";
import { deepToString, getEnv, memoize, withoutFragment } from "@solvari/utils";

import type {
  AddDataToProject,
  SentryProjectMetadata,
  SentryRootProject,
} from "@/lib/integrations/sentry/types.ts";

import { ignoreConfig } from "@/lib/integrations/sentry/ignored.ts";
import { sentryProjects } from "@/lib/integrations/sentry/projects.ts";

const applyDataToEvent =
  (addData?: AddDataToProject) =>
  (event: Readonly<SentryEvent>): SentryEvent => {
    if (!addData) {
      return event;
    }

    return mergeDeepRight(event, deepToString(addData()));
  };

const addGlobalData: AddDataToProject = () => ({
  tags: {
    envBE: getEnv().config.envBE,
    localeCode: getEnv().config.localeCode,
  },
  contexts: {
    dataLayer: { events: window.dataLayer ?? [] },
    argusEvents: { events: getArgusEvents() },
    metadata: { ...getClientSideArgusProjection() },
  },
});

const solvariContextIntegration = (
  rootProject: SentryRootProject,
): Integration => ({
  name: "SolvariContext",
  processEvent: pipe(
    applyDataToEvent(addGlobalData),
    applyDataToEvent(rootProject.addData),
  ),
});

const getEventModuleMeta = memoize(
  (event: SentryEvent) => {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;

    if (!frames) {
      return;
    }

    // Find the last frame with module metadata containing a project
    /* oxlint-disable typescript/no-unsafe-member-access */
    return [...frames]
      .toReversed()
      .find((frame) => frame.module_metadata?.project)?.module_metadata as
      | SentryProjectMetadata
      | undefined;
  },
  { key: (event) => event.event_id ?? nanoid(10), staleTime: 5000 },
);

const getEventProject = (event: SentryEvent) => {
  const moduleMeta = getEventModuleMeta(event);
  if (!moduleMeta?.project || !(moduleMeta.project in sentryProjects)) {
    return;
  }

  return moduleMeta as Required<SentryProjectMetadata>;
};

const solvariModuleMetaIntegration = (): Integration => ({
  name: "SolvariBuildType",
  processEvent(event) {
    const solvariProject = getEventModuleMeta(event);

    if (!solvariProject) {
      return event;
    }
    return applyDataToEvent(() => pick(solvariProject, ["tags", "contexts"]))(
      event,
    );
  },
});

const solvariMicroFeContextIntegration = (): Integration => ({
  name: "SolvariMicroFe",
  processEvent(event) {
    const solvariProject = getEventProject(event);

    if (!solvariProject) {
      return event;
    }

    return pipe(
      applyDataToEvent(() => ({
        tags: {
          project: event.tags?.project ?? solvariProject.project,
          package: solvariProject.project,
        },
      })),
      applyDataToEvent(globalThis.sentryProjects?.[solvariProject.project]),
    )(event);
  },
});

// The " (host)" suffix is added by Sentry's fetch wrapper, so the frame below it is the caller
const wrappedFetchFailurePattern =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \(.+\)$/;

// `denyUrls` only tests the wrapper frame, never the third-party caller
function isDeniedFetchFailure(event: SentryEvent) {
  const exception = event.exception?.values?.[0];
  const caller = exception?.stacktrace?.frames?.at(-2)?.filename;
  if (!exception?.value || !caller) {
    return false;
  }
  return (
    wrappedFetchFailurePattern.test(exception.value) &&
    ignoreConfig.denyUrls.some((pattern) => pattern.test(caller))
  );
}

// Chrome for iOS (CriOS) and the Google app (GSA) inject scripts into WebKit;
// WebKit attributes them to the HTML document, never to a script file
const googleIosUserAgentPattern = /\b(CriOS|GSA)\//;
const scriptFilePattern = /\.m?js(\?|#|$)/;

function isGoogleIosInjectedRecursion(event: SentryEvent) {
  const exception = event.exception?.values?.[0];
  if (
    exception?.type !== "RangeError" ||
    !exception.value?.startsWith("Maximum call stack size exceeded")
  ) {
    return false;
  }
  const userAgent = event.request?.headers?.["User-Agent"];
  if (!userAgent || !googleIosUserAgentPattern.test(userAgent)) {
    return false;
  }
  return !exception.stacktrace?.frames?.some(
    (frame) => frame.filename && scriptFilePattern.test(frame.filename),
  );
}

const solvariFilterIntegration = (): Integration => ({
  name: "SolvariFilter",
  processEvent(event) {
    if (
      event.exception?.values?.[0]?.value === "undefined" ||
      event.contexts?.browser?.name === "HeadlessChrome" ||
      isDeniedFetchFailure(event) ||
      isGoogleIosInjectedRecursion(event)
    ) {
      return null;
    }
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames?.length === 1 && frames[0]?.filename === "<anonymous>") {
      return null;
    }
    return event;
  },
});

const breadcrumbUrlKeys = ["from", "to", "url"] as const;

// Main-site `/bedankt#<payload>` carries lead PII in the fragment; inline-script frames carry the page URL.
const solvariUrlFragmentScrubIntegration = (): Integration => ({
  name: "SolvariUrlFragmentScrub",
  processEvent(event) {
    if (event.request?.url) {
      event.request.url = withoutFragment(event.request.url);
    }
    for (const exception of event.exception?.values ?? []) {
      for (const frame of exception.stacktrace?.frames ?? []) {
        if (frame.filename) {
          frame.filename = withoutFragment(frame.filename);
        }
        if (frame.abs_path) {
          frame.abs_path = withoutFragment(frame.abs_path);
        }
      }
    }
    for (const breadcrumb of event.breadcrumbs ?? []) {
      const data = breadcrumb.data;
      if (!data) {
        continue;
      }
      for (const key of breadcrumbUrlKeys) {
        const value: unknown = data[key];
        if (typeof value === "string") {
          data[key] = withoutFragment(value);
        }
      }
    }
    return event;
  },
});

export {
  getEventProject,
  solvariContextIntegration,
  solvariFilterIntegration,
  solvariMicroFeContextIntegration,
  solvariModuleMetaIntegration,
  solvariUrlFragmentScrubIntegration,
};
